"use client";

import {
  PROMO_DURATION_MS,
  firstPurchasePromoStore,
} from "@/store/firstPurchasePromo";
import { Modal } from "antd";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import Button from "../Button";
import CloseIcon from "./assets/close-icon.svg";
import modalBg from "./assets/modal-bg.jpg";

export interface FirstPurchasePromoModalProps {
  /** 是否显示弹窗 */
  open: boolean;
  /** 关闭弹窗回调 */
  onClose?: () => void;
  /** 点击"Claim it Now"按钮回调 */
  onClaim?: () => void;
  /** 倒计时结束时间（毫秒时间戳），默认为 15 分钟后 */
  endTime?: number;
}

interface CountdownTime {
  minutes: number;
  seconds: number;
}

/**
 * 首单优惠促销弹窗组件
 *
 * @example
 * ```tsx
 * const [open, setOpen] = useState(false);
 *
 * <FirstPurchasePromoModal
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   onClaim={() => {
 *     console.log('User clicked claim button');
 *     setOpen(false);
 *   }}
 * />
 * ```
 */
export const FirstPurchasePromoModal = observer(
  function FirstPurchasePromoModal({
    open,
    onClose,
    onClaim,
    endTime,
  }: FirstPurchasePromoModalProps) {
    const [countdown, setCountdown] = useState<CountdownTime>({
      minutes: 0,
      seconds: 0,
    });

    // 倒计时逻辑
    useEffect(() => {
      if (!open) {
        return;
      }

      // 计算结束时间（优先级：props.endTime > store.promoEndTime > 默认15分钟后）
      const targetTime =
        endTime ||
        firstPurchasePromoStore.promoEndTime ||
        Date.now() + PROMO_DURATION_MS;

      const updateCountdown = () => {
        const now = Date.now();
        const remaining = Math.max(0, targetTime - now);

        const minutes = Math.floor(remaining / 60000);
        const seconds = Math.floor((remaining % 60000) / 1000);

        setCountdown({ minutes, seconds });

        // 倒计时结束
        if (remaining <= 0) {
          onClose?.();
          // 如果是 15 分钟倒计时结束，清理 Store 中的时间，确保所有相关组件（如 Banner）同步隐藏
          if (firstPurchasePromoStore.isTimerActive) {
            firstPurchasePromoStore.clearPromoTimer();
          }
        }
      };

      updateCountdown();
      const timer = setInterval(updateCountdown, 1000);

      return () => {
        clearInterval(timer);
      };
    }, [open, endTime, firstPurchasePromoStore.promoEndTime, onClose]);

    const handleClaim = () => {
      onClaim?.();
    };

    return (
      <Modal
        open={open}
        onCancel={onClose}
        centered
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={null}
        width={360}
        classNames={{
          content: classNames("rounded-3xl! p-0! overflow-hidden!"),
        }}
        styles={{
          body: {
            padding: 0,
            height: 426,
            backgroundImage: `url(${modalBg.src})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          },
        }}
      >
        <div className="relative p-7 text-center h-[426px] flex flex-col items-center justify-between">
          <div
            className="absolute top-3 right-3 w-6 h-6 bg-black/[.04] flex items-center justify-center rounded-full cursor-pointer backdrop-blur-[13px] opacity-40 hover:bg-black/[.6]"
            onClick={onClose}
          >
            <CloseIcon />
          </div>
          <div>
            {/* 标题区 */}
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <h2 className="font-bold text-lg leading-normal bg-gradient-to-r from-[#FDA9FE] to-[#7B56E4] bg-clip-text text-transparent">
                FIRST PURCHASE
              </h2>
              <div className="px-1.5 py-1 bg-[#FF6752] rounded-[7px] font-bold text-xs leading-normal text-white">
                Limited Offer
              </div>
            </div>

            {/* 优惠折扣 */}
            <div className="flex flex-col items-center justify-center">
              <h1 className="font-extrabold text-[72px] leading-[86px] text-white">
                33% OFF
              </h1>
              <p className="font-semibold text-2xl text-[#BAA8FF] leading-[29px]">
                UP TO $631.2 OFF
              </p>
            </div>

            {/* 倒计时 */}
            <div className="flex items-center justify-center mt-6 gap-2">
              <div className="w-[53px] h-[43px] flex items-center justify-center bg-[rgba(136,101,168,0.77))] rounded-[10px] backdrop-blur-[10px] text-white">
                <span className="font-semibold text-base">
                  {countdown.minutes.toString().padStart(2, "0")}m
                </span>
              </div>
              <span className="font-semibold text-base">:</span>
              <div className="w-[53px] h-[43px] flex items-center justify-center bg-[rgba(136,101,168,0.77))] rounded-[10px] backdrop-blur-[10px] text-white">
                <span className="font-semibold text-base">
                  {countdown.seconds.toString().padStart(2, "0")}s
                </span>
              </div>
            </div>
          </div>

          <Button
            type="primary"
            onClick={handleClaim}
            className="w-full rounded-[10px] text-lg font-semibold"
          >
            Claim it Now
          </Button>
        </div>
      </Modal>
    );
  },
);
